// src/features/chat/templates/terminalTraceTemplates.js
/*
 * Terminal whispers and traces for approval artifacts.
 *
 * The former reason-code → sentence map is rewritten here as reason families:
 * each family carries a terse plain-language trace and, where re-proposing
 * helps, a recovery seed that the user can send as their own words. Failures
 * always speak; reason codes never reach the user.
 */

import { approvalReasonCode } from '../models/artifactLifecycleModel';
import {
  diffRowSummary,
  principalLabel,
  roleLabel,
  visibilityLabel,
} from './approvalCardTemplates';

const WHISPER_FRAGMENT_MAX_CHARS = 80;

function whisperFragment(text) {
  if (typeof text !== 'string' || !text.trim()) return null;
  const trimmed = text.trim();
  return trimmed.length > WHISPER_FRAGMENT_MAX_CHARS
    ? `${trimmed.slice(0, WHISPER_FRAGMENT_MAX_CHARS - 1)}…`
    : trimmed;
}

export const REASON_FAMILIES = Object.freeze({
  approved: 'decided_approved',
  rejected_by_user: 'decided_declined',

  stale_base_version: 'stale_conflict',
  base_version_changed: 'stale_conflict',
  projection_changed: 'stale_conflict',
  lifecycle_not_active: 'stale_conflict',
  conflict_operation_precondition: 'stale_conflict',
  conflict_target_display_changed: 'stale_conflict',
  conflict_audience_disclosure_changed: 'stale_conflict',
  approval_view_conflict: 'stale_conflict',
  already_changed: 'stale_conflict',
  source_ineligible: 'stale_conflict',
  link_target_ineligible: 'stale_conflict',
  target_unavailable: 'stale_conflict',

  approval_expired: 'expired',

  approval_execution_commit_failed: 'failed_clean',
  acl_commit_failed: 'failed_clean',
  visibility_commit_failed: 'failed_clean',

  acl_repair_required: 'repair_required',
  visibility_repair_required: 'repair_required',
  acl_commit_incomplete: 'repair_required',
  visibility_commit_incomplete: 'repair_required',
  source_cleanup_incomplete: 'repair_required',

  phase4_disabled: 'disabled',
  phase5_disabled: 'disabled',
  boundary_helper_unavailable: 'disabled',
  approval_execution_disabled: 'disabled',

  approval_execution_in_flight: 'in_flight',
  approval_execution_in_progress: 'in_flight',

  record_admin_required: 'not_allowed',
  group_membership_required: 'not_allowed',

  schema_invalid: 'unsafe',
  control_invalid: 'unsafe',
  records_approval_payload_invalid: 'unsafe',
  approval_executable_state_invalid: 'unsafe',

  side_effect_warning: 'side_effect_warning',
});

/**
 * Recovery seed asking for a fresh proposal, phrased per operation.
 * @param {Object} approval
 * @returns {{label: string, text: string}}
 */
export function reProposalSeed(approval) {
  const texts = {
    'record.update': 'Propose that Record update again',
    'record.delete': 'Propose deleting that Record again',
    'record.access.grant': 'Propose sharing that Record again',
    'record.access.role_change': 'Propose that access change again',
    'record.access.revoke': 'Propose removing that access again',
    'record.visibility.set': 'Propose that visibility change again',
  };
  return {
    label: 'Ask again',
    text: texts[approval?.operationKind] || 'Propose that change again',
  };
}

const FAMILY_TRACES = {
  stale_conflict: (approval) => ({
    glyph: 'dot',
    text: 'Couldn’t apply — things changed since this was proposed',
    seed: reProposalSeed(approval),
  }),
  expired: (approval) => ({
    glyph: 'dot',
    text: 'This offer expired before it was used',
    seed: reProposalSeed(approval),
  }),
  failed_clean: (approval) => ({
    glyph: 'dot',
    text: 'Couldn’t apply — nothing changed',
    seed: reProposalSeed(approval),
  }),
  repair_required: () => ({
    glyph: 'dot',
    text: 'Couldn’t finish — some of this may have applied',
    seed: {
      label: 'Ask Affin to check',
      text: 'Check what happened with that change and put it right',
    },
  }),
  disabled: () => ({
    glyph: 'dot',
    text: 'Couldn’t apply — this kind of change is unavailable right now',
  }),
  not_allowed: () => ({
    glyph: 'dot',
    text: 'Couldn’t apply — you no longer have the access this needed',
  }),
  unsafe: (approval) => ({
    glyph: 'dot',
    text: 'Couldn’t apply this safely',
    seed: reProposalSeed(approval),
  }),
};

const STATE_FALLBACK_TRACES = {
  unavailable: (approval) => FAMILY_TRACES.stale_conflict(approval),
  failed: (approval) => FAMILY_TRACES.failed_clean(approval),
  expired: (approval) => FAMILY_TRACES.expired(approval),
};

/**
 * Plain-language terminal trace for a settled, non-resolved approval
 * (conflict / failure / expiry / unavailable).
 * @param {Object} approval
 * @returns {{glyph: string, text: string, seed?: {label: string, text: string}}}
 */
export function terminalTrace(approval) {
  const reason = approvalReasonCode(approval);
  const family = reason ? REASON_FAMILIES[reason] : null;
  if (family && FAMILY_TRACES[family]) {
    return FAMILY_TRACES[family](approval);
  }
  const fallback = STATE_FALLBACK_TRACES[approval?.status];
  if (fallback) return fallback(approval);
  return FAMILY_TRACES.stale_conflict(approval);
}

/**
 * Past-tense outcome whisper once an approval resolves.
 * @param {Object} approval
 * @returns {{glyph: 'check'|'cross', text: string, warning?: string}}
 */
export function approvalWhisper(approval) {
  if (approval?.status === 'rejected') {
    return { glyph: 'cross', text: 'Skipped — nothing changed' };
  }

  let text;
  switch (approval?.operationKind) {
    case 'record.access.grant': {
      const role = approval?.accessChange?.after?.role;
      text = role
        ? `Shared with ${principalLabel(approval)} — ${roleLabel(role).toLowerCase()}`
        : `Shared with ${principalLabel(approval)}`;
      break;
    }
    case 'record.access.role_change':
      text = `${principalLabel(approval)}’s access changed`;
      break;
    case 'record.access.revoke':
      text = `${principalLabel(approval)}’s access removed`;
      break;
    case 'record.visibility.set': {
      const after = approval?.accessChange?.after?.visibility;
      text = after === 'private'
        ? 'Record is private again'
        : after
          ? `Record now visible to ${visibilityLabel(after).toLowerCase()}`
          : 'Record visibility changed';
      break;
    }
    case 'record.delete':
      text = 'Record deleted';
      break;
    default: {
      if (approval?.kind === 'record_delete') {
        text = 'Record deleted';
        break;
      }
      // The whisper names what changed (from the commit-pinned diff), not
      // just that something changed.
      const rows = Array.isArray(approval?.semanticDiff) ? approval.semanticDiff : [];
      const fragment = rows.length ? whisperFragment(diffRowSummary(rows[0])) : null;
      text = fragment ? `Update applied — ${fragment}` : 'Update applied';
      break;
    }
  }

  const warningReason =
    approval?.warningReason || approval?.result?.warningReason || null;
  if (warningReason === 'side_effect_warning') {
    return {
      glyph: 'check',
      text,
      warning: 'The notification couldn’t be sent, though',
    };
  }
  return { glyph: 'check', text };
}

/**
 * Plain-language notice when an artifact action call fails outright.
 * @param {Object} errorOrResult - Callable error or unsuccessful result.
 * @returns {string}
 */
export function actionErrorText(errorOrResult) {
  const details = errorOrResult?.details || errorOrResult || {};
  const reason =
    details.reason ||
    details.unavailableReason ||
    details.failureReason;
  const family = reason ? REASON_FAMILIES[reason] : null;
  if (family === 'in_flight') {
    return 'That one is already being applied.';
  }
  if (family) {
    const trace = FAMILY_TRACES[family] || FAMILY_TRACES.stale_conflict;
    return trace({}).text;
  }
  const code = String(errorOrResult?.code || '').replace(/^functions\//, '');
  if (code === 'failed-precondition') {
    return 'Affin can’t do this right now.';
  }
  if (code === 'unauthenticated') {
    return 'Please sign in to do this.';
  }
  if (code === 'permission-denied') {
    return 'You don’t have permission to do this.';
  }
  return 'That didn’t go through — nothing changed.';
}
